type HoldPublicRenderArgs = {
  sessionToken: string | undefined;
  isAuthDataReady: boolean;
  hasAuthError: boolean;
  pathname: string;
  routePattern: string;
  loggedoutPaths: string[];
};

/**
 * Whether a public route's render should be held behind `LoadingView`.
 *
 * Public routes whose settled appearance for a logged-in user is not what
 * they render on their own: `/login`/`/register` are about to be navigated
 * away from by `authCheck`, and the 404 routes are waiting for the profile
 * that lets `DashboardLayout` draw its chrome. Rendering them immediately
 * shows a usable login form, or a bare chrome-less 404, that is replaced
 * seconds later — so hold `LoadingView` until the session data settles.
 *
 * A pure function of its inputs, safe to compute during SSR: `getToken` reads
 * the session cookie isomorphically, so the server and the hydrating client
 * agree on `sessionToken` and render the same thing. Released on any auth
 * error so a dead token can't hold the spinner forever.
 */
export default function shouldHoldPublicRender({
  sessionToken,
  isAuthDataReady,
  hasAuthError,
  pathname,
  routePattern,
  loggedoutPaths,
}: HoldPublicRenderArgs): boolean {
  return (
    !!sessionToken &&
    !isAuthDataReady &&
    !hasAuthError &&
    (loggedoutPaths.includes(pathname) ||
      routePattern === "/$" ||
      routePattern === "/404")
  );
}
